import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, ArrowUpRight } from 'lucide-react';
import Footer from '../components/Footer';

const DressCodesProject = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const galleryImages = [
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/c7sji68f__6II8385.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/ozzzzt1g__6II7172.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/f7kfd5ht__6II7297.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/2qyycce1__6II7382.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/s26ygqwf__6II7819.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/gbraasj6__6II6816.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/eckgnybx__6II9226-HDR.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/urycfswg__6II8571.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/04qubgxj__6II9161-Edit.jpg',
    'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/uy7rdzrw__6II9217.jpg',
  ];

  const copyright = "© Historic Royal Palaces";

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="bg-dark min-h-screen">
      {/* Back Button */}
      <div className="fixed top-24 left-6 md:left-12 z-40">
        <Link 
          to="/#projects" 
          className="inline-flex items-center gap-2 text-cream hover:text-cream/70 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm uppercase tracking-wider">Back</span>
        </Link>
      </div>

      {/* Title Section */}
      <section className="pt-32 pb-8 px-6 md:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl uppercase text-cream mb-4"
            style={{ fontFamily: 'Anton, sans-serif' }}
          >
            Dress Codes
          </h1>
          <p className="text-xl md:text-2xl text-cream">
            Kensington Palace
          </p>
        </div>
      </section>

      {/* Full-width Carousel Gallery */}
      <section className="py-8">
        <div className="relative w-full">
          {/* Main Image */}
          <div className="relative w-full flex justify-center" style={{ height: '70vh' }}>
            <div className="relative h-full">
              <img 
                src={galleryImages[currentImageIndex]} 
                alt={`Dress Codes Exhibition ${currentImageIndex + 1}`}
                className="h-full w-auto object-contain"
              />
              
              {/* Copyright overlay - inside image */}
              <div className="absolute bottom-4 right-4 bg-black/60 px-3 py-1 rounded">
                <span className="text-sm text-cream">{copyright}</span>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button 
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-cream/20 hover:bg-cream/40 rounded-full flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={28} className="text-cream" />
            </button>
            <button 
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-cream/20 hover:bg-cream/40 rounded-full flex items-center justify-center transition-colors"
            >
              <ChevronRight size={28} className="text-cream" />
            </button>
          </div>

          {/* Image Counter */}
          <div className="text-center py-4">
            <span className="text-cream text-sm">
              {currentImageIndex + 1} / {galleryImages.length}
            </span>
          </div>

          {/* Thumbnail Strip */}
          <div className="flex justify-center gap-2 px-6 overflow-x-auto pb-4">
            {galleryImages.map((image, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden transition-all ${
                  idx === currentImageIndex ? 'ring-2 ring-cream' : 'opacity-50 hover:opacity-100'
                }`}
              >
                <img 
                  src={image} 
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Text Content */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <p className="text-xl md:text-2xl leading-relaxed text-cream mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            A creative engagement programme, where young people were supported to develop and produce their own films, costumes, and music for public display.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-cream mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Film production and public-facing engagement for the major national exhibition. Shaped the exhibition film and participant-led outputs, bridging curatorial intent with audience experience.
          </p>
          
          {/* Project Services */}
          <h2 
            className="text-2xl md:text-3xl uppercase text-cream mb-6 mt-12"
            style={{ fontFamily: 'Anton, sans-serif' }}
          >
            Project Services
          </h2>
          <ul className="space-y-3 text-cream text-lg mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            <li>• Led full production, from concept development through to final installation</li>
            <li>• Directed and produced the introductory exhibition film, anchoring the visitor journey</li>
            <li>• Designed and delivered a creative learning programme, equipping young people with filmmaking, storytelling, and animation skills</li>
            <li>• Guided participants to develop and author their own films, ensuring meaningful creative ownership</li>
            <li>• Translated curatorial themes into accessible, audience-facing content at scale</li>
          </ul>

          {/* Output */}
          <h2 
            className="text-2xl md:text-3xl uppercase text-cream mb-6 mt-12"
            style={{ fontFamily: 'Anton, sans-serif' }}
          >
            Output
          </h2>
          <ul className="space-y-2 text-cream text-lg mb-12" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            <li>1 x introductory installation film</li>
            <li>1 x BTS film screened live at Kensington Palace fashion event</li>
            <li>5 x co-created films by young people, exhibited as part of the show</li>
          </ul>

          {/* External Link */}
          <a 
            href="https://www.hrp.org.uk/kensington-palace/whats-on/dress-codes/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-cream hover:text-cream/70 transition-colors text-lg underline"
          >
            Visit Historic Royal Palaces Website
            <ArrowUpRight size={20} />
          </a>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 px-6 md:px-12 bg-dark">
        <div className="max-w-5xl mx-auto">
          <div className="aspect-video bg-dark rounded-lg overflow-hidden">
            <iframe
              src="https://player.vimeo.com/video/1074748388"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              className="w-full h-full"
              title="Dress Codes Exhibition Film"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DressCodesProject;
